"use client";

import { useQuery, useQuery_experimental, type OptionalRestArgsOrSkip } from "convex/react";
import type { FunctionArgs, FunctionReference, FunctionReturnType } from "convex/server";
import { convexToJson } from "convex/values";
import { useLastLoaded } from "@/hooks/use-last-loaded";

type NowQuery = FunctionReference<"query", "public", { now?: number }>;

function useHeldAcrossNow<Value>(
  fresh: Value | undefined,
  queryArgs: { now?: number } | "skip" | undefined,
): Value | undefined {
  const identity =
    queryArgs === "skip" ? "skip" : JSON.stringify(convexToJson({ ...queryArgs, now: 0 }));
  const held = useLastLoaded(fresh, identity);
  return identity === "skip" ? undefined : held;
}

export function useNowQuery<Query extends NowQuery>(
  query: Query,
  ...args: OptionalRestArgsOrSkip<Query>
): FunctionReturnType<Query> | undefined {
  const [queryArgs] = args;
  return useHeldAcrossNow(useQuery(query, ...args), queryArgs);
}

export function useOptionalNowQuery<Query extends NowQuery>(
  query: Query,
  args: FunctionArgs<Query> | "skip",
): FunctionReturnType<Query> | undefined {
  const result = useQuery_experimental({ query, args });
  return useHeldAcrossNow(result.status === "success" ? result.data : undefined, args);
}
